import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button } from '../Button';
import './InterstitialModal.scss';

// ─── InterstitialModal ────────────────────────────────────────────────────────
// Pure UI component. Render it controlled (open/onCancel/onConfirm) or let
// InterstitialProvider manage state automatically via the .js-interstitial class.

export const InterstitialModal = ({
  open,
  href,
  title = "You\u2019re about to leave the More to Parkinson's website",
  bodyText = 'You will be redirected to a product website with information on a potential treatment option.',
  continueText = 'Do you want to continue?',
  confirmLabel = 'Leave site',
  cancelLabel = 'Stay here',
  onConfirm,
  onCancel,
}) => {
  const closeRef = useRef(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const dialog = dialogRef.current;
    const close = () => typeof onCancel === 'function' && onCancel();

    // Query focusable elements inside the dialog at call time so dynamic content is handled.
    const getFocusable = () => {
      if (!dialog) return [];
      const selector = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',');
      return Array.from(dialog.querySelectorAll(selector)).filter(
        (el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true'
      );
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }

      if (event.key !== 'Tab') return;

      const items = getFocusable();
      if (items.length === 0) {
        event.preventDefault();
        dialog?.focus();
        return;
      }

      const first = items[0];
      const last = items.at(-1);
      const active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || !dialog?.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last || !dialog?.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    };

    const handleFocusIn = (event) => {
      if (!dialog?.contains(event.target)) {
        const items = getFocusable();
        (items[0] || dialog)?.focus({ preventScroll: true });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('focusin', handleFocusIn, true);
    closeRef.current?.focus();

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('focusin', handleFocusIn, true);
    };
  }, [open, onCancel]);

  if (!open) return null;

  const handleConfirm = () => {
    if (typeof onConfirm === 'function') {
      onConfirm();
      return;
    }
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="interstitial-modal__backdrop">
      {/* Focusable full-screen backdrop — closes modal on click */}
      <button
        type="button"
        className="interstitial-modal__backdrop-btn"
        aria-label="Close dialog"
        onClick={onCancel}
        tabIndex={-1}
      />

      <div
        ref={dialogRef}
        className="interstitial-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="interstitial-modal-title"
        aria-describedby="interstitial-modal-desc"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Escape') typeof onCancel === 'function' && onCancel();
        }}
      >
        {/* Close × button */}
        <button
          ref={closeRef}
          type="button"
          className="interstitial-modal__close"
          aria-label="Close dialog"
          onClick={onCancel}
        >
          <img src="/images/close-x.svg" alt="" aria-hidden="true" />
        </button>

        <h2 id="interstitial-modal-title" className="interstitial-modal__title">
          {title}
        </h2>

        <p id="interstitial-modal-desc" className="interstitial-modal__body">
          {bodyText}
        </p>

        {continueText && (
          <h3 className="interstitial-modal__continue">{continueText}</h3>
        )}

        <div className="interstitial-modal__actions">
          <button
            type="button"
            className="interstitial-modal__cancel"
            onClick={onCancel}
          >
            {cancelLabel}
          </button>

          <Button variant="primary" icon="arrow" onClick={handleConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

InterstitialModal.propTypes = {
  /** Controls modal visibility */
  open: PropTypes.bool,
  /** Destination URL — opened in a new tab on confirm */
  href: PropTypes.string,
  /** Modal headline */
  title: PropTypes.string,
  /** Descriptive body paragraph */
  bodyText: PropTypes.string,
  /** Bold subheading above the action buttons */
  continueText: PropTypes.string,
  /** Label for the primary "leave" button */
  confirmLabel: PropTypes.string,
  /** Label for the secondary "stay" button */
  cancelLabel: PropTypes.string,
  /** Override default confirm behaviour (e.g. for testing) */
  onConfirm: PropTypes.func,
  /** Called when the user dismisses the modal */
  onCancel: PropTypes.func,
};

// ─── InterstitialProvider ─────────────────────────────────────────────────────
// Mount ONCE in your layout (gatsby-browser.js / wrapPageElement).
// Any <a class="js-interstitial" href="…"> click is intercepted automatically.
//
// Usage in markup:
//   <a href="https://nuplazid.com" class="js-interstitial">Learn more</a>
//   <a href="https://instagram.com" class="js-interstitial" data-interstitial-type="social">Instagram</a>
//
// Usage in JSX:
//   <a href="https://nuplazid.com" className="js-interstitial">Learn more</a>
//   <a href="https://instagram.com" className="js-interstitial" data-interstitial-type="social">Instagram</a>
//
// Types:
//   - default: shows product website message
//   - social: shows third-party social media website message

export const InterstitialProvider = ({
  title,
  bodyText,
  continueText,
  confirmLabel,
  cancelLabel,
}) => {
  const [modalState, setModalState] = useState({ open: false, href: '', type: '' });

  useEffect(() => {
    const handleClick = (e) => {
      const anchor = e.target.closest('a.js-interstitial');
      if (!anchor) return;
      e.preventDefault();
      const type = anchor.getAttribute('data-interstitial-type') || 'default';
      setModalState({ open: true, href: anchor.href, type });
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const handleConfirm = () => {
    window.open(modalState.href, '_blank', 'noopener,noreferrer');
    setModalState({ open: false, href: '', type: '' });
  };

  const handleCancel = () => {
    setModalState({ open: false, href: '', type: '' });
  };

  // Determine body text based on link type
  const getBodyText = () => {
    if (modalState.type === 'social') {
      return 'You will be redirected to a third-party social media website.';
    }
    return bodyText;
  };

  return (
    <InterstitialModal
      open={modalState.open}
      href={modalState.href}
      title={title}
      bodyText={getBodyText()}
      continueText={continueText}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );
};

InterstitialProvider.propTypes = {
  title: PropTypes.string,
  bodyText: PropTypes.string,
  continueText: PropTypes.string,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
};

export default InterstitialModal;
